import io, { Socket } from 'socket.io-client';
import { useState, useEffect, useRef, useCallback } from 'react';

import { useDebounceFn } from '@frontend/features/common/hooks/useDebounceFn';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { CONFIG } from '@frontend/config';

export type SendMessageCallback = (event: string, data: string) => void;

interface UseWsOptions {
  onConnected?: () => void;
}

export const useWS = (url: string, options?: UseWsOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuth();
  const socketRef = useRef<Socket>();
  const onConnected = options?.onConnected;

  const connect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    socketRef.current = io(url, {
      transports: ['websocket'],
      path: '/v1/api/socket.io',
      query: { agencyId: CONFIG.AGENCY_ID },
    });
    socketRef.current?.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      onConnected && onConnected();
    });

    socketRef.current?.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socketRef.current?.on('connect_error', (err: Error) => {
      console.error('WebSocket error', err);
      setIsConnected(false);
    });
  }, [url]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('Manually disconnecting WebSocket');
      socketRef.current.disconnect();
      setIsConnected(false);
    } else {
      console.log('No active WebSocket connection to disconnect');
    }
  }, []);

  const sendMessage = useCallback<SendMessageCallback>(
    (event, data) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit(event, data);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected],
  );

  const joinRoom = useCallback(
    (roomId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('joinRoom', roomId);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected, socketRef],
  );

  const leaveRoom = useCallback(
    (roomId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('leaveRoom', roomId);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected],
  );

  useEffect(() => {
    console.log('[useWS] useEffect mount');

    if (!isAuthenticated) {
      console.log('[useWS] skipping connect: not authenticated');
      return;
    }

    connect();

    return () => {
      console.log('[useWS] useEffect cleanup (unmount)');
      if (socketRef.current && isConnected) {
        socketRef.current.disconnect();
        setIsConnected(false);
      }
    };
  }, [url, isAuthenticated]);

  const sendMessageDebounced = useDebounceFn(sendMessage, 500);

  return { isConnected, sendMessage, sendMessageDebounced, connect, disconnect, socketRef, joinRoom, leaveRoom };
};
